import { ArrowLeft } from 'lucide-react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

const sections: FAQSection[] = [
  {
    title: 'Discovering Restaurants',
    items: [
      {
        id: 'filter-feed',
        question: 'How do I filter the review feed?',
        answer:
          'Use the filter bar at the top of the home screen to narrow results by cuisine, rating, or distance. Tap any filter chip to toggle it on or off.',
      },
      {
        id: 'save-restaurant',
        question: 'How do I save restaurants?',
        answer:
          'Tap the bookmark icon on any restaurant card or restaurant page to add it to your personal saved list. You\'ll earn 10 Kudoz as a bonus the very first time you save a spot.',
      },
      {
        id: 'crown-icon',
        question: 'What does the crown icon mean?',
        answer:
          'The crown marks dishes and restaurants the community has rated as outstanding. It\'s the "The Best" badge — awarded automatically when something earns consistently high praise across multiple reviews.',
      },
    ],
  },
  {
    title: 'Reviews',
    items: [
      {
        id: 'dine-in-review',
        question: 'How do I review a dine-in meal?',
        answer:
          'Tap the camera button at the bottom of the screen and choose "Dine In". You\'ll be able to rate the atmosphere, individual dishes, and the service.',
      },
      {
        id: 'delivery-takeout-review',
        question: 'How do I review a past delivery or takeout order?',
        answer:
          'Tap the camera button and choose "Delivery" or "Takeout". You can log any past order — it doesn\'t have to have been placed through the app — and you\'ll earn Kudoz for sharing your experience.',
      },
      {
        id: 'camera-review',
        question: 'Can I start a review with a photo or video?',
        answer:
          'Yes. Tap the camera button, snap a shot or record a clip, and the app will suggest the restaurant and relevant tags automatically. If you\'d rather skip the camera, tap the pencil icon to write your review directly.',
      },
    ],
  },
  {
    title: 'Kudoz & Rewards',
    items: [
      {
        id: 'earn-kudoz',
        question: 'How do I earn Kudoz?',
        answer:
          'You earn Kudoz by writing reviews, completing your profile, placing orders through the app, saving your first restaurant, and referring friends who sign up using your link.',
      },
      {
        id: 'redeem-kudoz',
        question: 'How do I redeem Kudoz?',
        answer:
          'Go to the Rewards screen (the gift icon in the bottom nav) and tap Redeem. Your available Kudoz balance will be applied as a discount on your next order.',
      },
      {
        id: 'referral-bonus',
        question: 'How does the referral bonus work?',
        answer:
          'Share your personal review link with a friend. When they sign up using your link, you both receive bonus Kudoz credited to your accounts automatically.',
      },
    ],
  },
  {
    title: 'Ordering',
    items: [
      {
        id: 'order-in-app',
        question: 'Can I order food directly in the app?',
        answer:
          'Yes — on any restaurant page, browse the menu and tap "Order" on any item to start a takeout or delivery order. You\'ll complete payment without leaving the app, and your order status updates in real time.',
      },
    ],
  },
];

export default function FAQ() {
  const [, navigate] = useLocation();

  function handleBack() {
    const beforeUrl = window.location.href;
    window.history.back();
    setTimeout(() => {
      if (window.location.href === beforeUrl) {
        navigate('/profile');
      }
    }, 150);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="flex items-center gap-2 px-4 h-14">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            aria-label="Back"
            data-testid="button-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-semibold text-lg">Help &amp; FAQ</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-4 py-6 pb-24 space-y-8 max-w-2xl mx-auto w-full">
        {sections.map((section) => (
          <div key={section.title}>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              {section.title}
            </h2>
            <Accordion type="multiple" className="space-y-1">
              {section.items.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border border-border rounded-md px-4 data-[state=open]:bg-muted/30"
                >
                  <AccordionTrigger className="text-sm font-medium text-left py-4 hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
}
